import { useEffect, useRef } from "react";
import { TURNSTILE_SITE_KEY } from "../lib/config";

interface TurnstileApi {
  render: (el: HTMLElement, opts: { sitekey: string; callback: (token: string) => void; "expired-callback"?: () => void }) => string;
  remove: (id: string) => void;
}
declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

function ensureScript(): void {
  if (document.querySelector(`script[src="${SCRIPT_SRC}"]`)) return;
  const s = document.createElement("script");
  s.src = SCRIPT_SRC;
  s.async = true;
  s.defer = true;
  document.head.appendChild(s);
}

interface Props {
  /** Called with the Turnstile token on success, or "" when Turnstile is unconfigured (dark). */
  onToken: (token: string) => void;
}

/**
 * Cloudflare Turnstile widget (bot protection). Feature-flagged: when no site key is configured
 * (the current dark state — keys land in KV via F1.5-5), the widget renders nothing and reports an
 * empty token. The backend verifier fails open while unconfigured, so the form still works.
 */
export default function TurnstileWidget({ onToken }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) {
      onToken(""); // unconfigured — no challenge; backend fails open
      return;
    }
    ensureScript();
    let widgetId: string | undefined;
    let cancelled = false;
    const tryRender = () => {
      if (cancelled || !ref.current || !window.turnstile) return false;
      widgetId = window.turnstile.render(ref.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => onToken(token),
        "expired-callback": () => onToken(""),
      });
      return true;
    };
    if (!tryRender()) {
      const interval = window.setInterval(() => {
        if (tryRender()) window.clearInterval(interval);
      }, 200);
      return () => {
        cancelled = true;
        window.clearInterval(interval);
        if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
      };
    }
    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [onToken]);

  if (!TURNSTILE_SITE_KEY) return null;
  return <div ref={ref} className="my-2" aria-label="Bot protection challenge" />;
}
